import { Component, OnInit, OnDestroy, HostListener, NgZone, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import { BusinessEntityService } from '../../../core/services/business.service';
import BusinessEntity, { Address } from '../../../core/models/business.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FarmSiteService } from '../../../core/services/farm-site.service';
import FarmSiteModel, {
  BioSecurity,
  Contract,
  IWaterType,
  ISoilType,
  IAddress,
  SMEContract,
  BuyerContract
} from '../../../core/models/farm-site.model';
import { WaterTypeService } from '../../../core/services/water-type.service';
import WaterTypeModel from '../../../core/models/water-type.model';
import { SoilTypeService } from '../../../core/services/soil-type.service';
import SoilTypeModel from '../../../core/models/soil-typeModel';
import * as _ from 'lodash';
import * as moment from 'moment';
import { CustomerPondService } from '../../../core/services/customer-pond.service';
import CustomerPondModel from '../../../core/models/customer-pond.model';
import PersonsModel from '../../../core/models/persons.model';
import { PersonsService } from '../../../core/services/persons.service';
import ImageDetailsModel from 'src/app/core/models/images-details.model';
import { ImageDetailsService } from 'src/app/core/services/images-details.service';
import { BusinessTypeService } from '../../../core/services/business-type.service';
import BusinessTypeModel from '../../../core/models/business-type.model';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { CountriesAPIService } from 'src/app/core/services/countries.service';
import { StatesAPIService } from 'src/app/core/services/states.service';
import { StateModel, VillageModel } from 'src/app/core/models/json.model';
import CountriesModel from 'src/app/core/models/countries.model';
import { BatchLoader } from "../../../core/utilities/batch-loader.util";
import RoutesModel from "../../../core/models/route.model";

declare var $: any;

/**
 * @component CustomerLocationsComponent
 * @selector app-business-entity-farms
 * @implements {OnInit, OnDestroy}
 *
 * @description
 * The `CustomerLocationsComponent` manages the creation, updating, and listing of Farm Sites associated with a Business Entity.
 * It serves as a comprehensive form and dashboard for managing farm infrastructure details, contracts, and location data.
 *
 * ## Key Responsibilities:
 * 1. **Farm Site Management**: Allows users to create new farm sites or update existing ones with details like name, area, and year started.
 * 2. **Location Management**: Handles hierarchical location selection (Country -> State -> Village) and geocoding (Pincode -> Lat/Long).
 * 3. **Contract Management**: Manages relationships and contracts with Feed Providers, SMEs, and Buyers.
 * 4. **Bio-Security Configuration**: Allows configuration of bio-security measures (Fencing, Reservoirs, etc.).
 * 5. **Water & Soil Details**: Captures environmental parameters for the farm site.
 *
 * ## User Roles & Permissions:
 * - **Business Admin (Provider)**: Can manage farm sites for their client farms.
 * - **Farmer / Farm Manager**: Can manage their own farm sites.
 * - **Admin**: System-wide access.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: Logged-in business ID.
 * - `personID`: Logged-in user ID.
 * - `loginBusinessIcon`: Cached business icon path.
 *
 * @service BusinessEntityService - For business entity operations.
 * @service FarmSiteService - For farm site CRUD operations.
 * @service WaterTypeService - For fetching water types.
 * @service SoilTypeService - For fetching soil types.
 * @service PersonsService - For fetching associated persons (SMEs, Buyers).
 * @service CountriesAPIService - For country data.
 * @service StatesAPIService - For state and village data.
 * @service CommonMethodsService - For error handling.
 */
@Component({
  selector: 'app-customer-locations',
  templateUrl: './customer-locations.component.html',
  styleUrls: ['./customer-locations.component.scss']
})
export class CustomerLocationsComponent implements OnInit, OnDestroy {
  /** @type {BusinessEntity} The main business entity model. */
  public businessEntity: BusinessEntity;

  /** @type {Array<BusinessEntity>} List of business entities. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {Array<BusinessTypeModel>} List of business types. */
  public businessTypes: Array<BusinessTypeModel>;

  /** @type {Array<FarmSiteModel>} List of farm sites. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel[]} List of farm sites (duplicate/alias). */
  public farmSitesList: FarmSiteModel[] = [];

  /** @type {FarmSiteModel} Current farm site model being created/edited. */
  public farmSite: FarmSiteModel = new FarmSiteModel();

  /** @type {RoutesModel[]} Filtered list of farm sites for display. */
  public filteredFarmSites: RoutesModel[] = [];

  /** @type {BioSecurity} Bio-security configuration model. */
  public bioSecurity: BioSecurity;

  /** @type {Contract} Contract model for feed providers. */
  public contract: Contract;

  /** @type {SMEContract} Contract model for SMEs. */
  public smeContract: SMEContract;

  /** @type {BuyerContract} Contract model for Buyers. */
  public buyerContract: BuyerContract;

  /** @type {IWaterType} Water type configuration. */
  public waterType: IWaterType;

  /** @type {ISoilType} Soil type configuration. */
  public soilType: ISoilType;

  /** @type {IAddress} Address model for the farm site. */
  public address: IAddress;

  /** @type {Array<WaterTypeModel>} List of available water types. */
  public waterTypes: Array<WaterTypeModel>;

  /** @type {Array<SoilTypeModel>} List of available soil types. */
  public soilTypes: Array<SoilTypeModel>;

  /** @type {Array<CustomerPondModel>} List of customer ponds. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<PersonsModel>} List of persons (generic). */
  public persons: Array<PersonsModel>;

  /** @type {Array<PersonsModel>} List of buyer persons. */
  public buyerPersons: Array<PersonsModel>;

  /** @type {Array<PersonsModel>} List of persons associated with business. */
  public personsByBusinessId: Array<PersonsModel>;

  /** @type {PersonsModel} Single person model. */
  public person: PersonsModel;

  /** @type {Array<ImageDetailsModel>} List of image details. */
  public imageDetails: Array<ImageDetailsModel>;

  /** @type {ImageDetailsModel} Single image detail model. */
  public imageDetail: ImageDetailsModel;

  /** @type {string} Business ID from route parameters. */
  public paramsbusinessId: string;

  /** @type {string} Navigation parameter for business ID. */
  public navparamBusinessID: string;

  /** @type {boolean} Page loading state flag. */
  public pgLoad = false;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {FormGroup} Reactive form for adding/editing farm sites. */
  public addFarmsiteForm: FormGroup;

  /** @type {boolean} Flag indicating if form is submitted. */
  public submitted = false;

  /** @type {boolean} Flag indicating if pond form is submitted. */
  public pondSubmitted = false;

  /** @type {string} ID of the farm business. */
  public farmbusinessid: string;

  /** @type {Array<any>} Array of years for 'Year Started' dropdown. */
  public yearStartedArr = new Array();

  /** @type {boolean} Flag for SME contract selection. */
  public isSMEChecked: boolean;

  /** @type {boolean} Flag for Buyer contract selection. */
  public isBuyerChecked: boolean;

  /** @type {boolean} Validation flag for water spread area. */
  public waterareavalidation: boolean;

  /** @type {Array<any>} Array of month names. */
  public strArray = new Array();

  /** @type {Array<any>} List of villages. */
  public villagesList = new Array();

  /** @type {boolean} Flag to display postal info. */
  public isPostalInfoDisp = true;

  /** @type {boolean} Flag indicating invalid pincode. */
  public isPinNotValid = false;

  /** @type {boolean} Bio-security: Fencing. */
  public bioFencing: boolean;

  /** @type {boolean} Bio-security: Hand Wash & Footbath. */
  public bioHWandFA: boolean;

  /** @type {boolean} Bio-security: Crab Fencing. */
  public bioCrabFencing: boolean;

  /** @type {boolean} Bio-security: Reservoirs. */
  public bioReservoirs: boolean;

  /** @type {boolean} Bio-security: Bird Fencing. */
  public bioBirdFencing: boolean;

  /** @type {boolean} Bio-security: Bio Filters. */
  public bioFilters: boolean;

  /** @type {boolean} Flag to show submit button. */
  public submitButton: boolean;

  /** @type {boolean} Flag to show update button. */
  public updateButton: boolean;

  /** @type {string} Logged-in business ID. */
  public loginBusinessID: string;

  /** @type {string} Logged-in business category. */
  public loginUserBusinessCategory: string;

  // ************Update Farm site declarations **************
  /** @type {string} Selected year started. */
  public selectedYear: string;

  /** @type {Array<string>} Array of pond IDs for deletion. */
  public pondArr: Array<string>;

  /** @type {string} Selected data type for deletion. */
  public selectedDeletedataType: string;

  /** @type {string} Country code. */
  public countryCode: string;

  /** @type {BusinessEntity} Pincode validation result. */
  public pinCodes: BusinessEntity;

  /** @type {Array<BusinessEntity>} List of SME businesses. */
  public smeBusinessEntities: Array<BusinessEntity>;

  /** @type {Array<BusinessEntity>} List of Buyer businesses. */
  public buyerBusinessEntities: Array<BusinessEntity>;

  /** @type {Array<PersonsModel>} List of provider persons. */
  public providerPersons: Array<PersonsModel>;

  /** @type {any} Business parameters. */
  public paramsBusiness: any;

  /** @type {any} Application permissions. */
  public appPermission: any;

  /** @type {boolean} Validation flag for grow-out ponds. */
  public growOutPondsValidation: boolean;

  /** @type {boolean} Validation flag for grow-out ponds digits. */
  public growOutPondsDigits: boolean;

  /** @type {FarmSiteModel} Lat/Long data. */
  public latLongs: FarmSiteModel;

  /** @type {Array<any>} List of countries. */
  public countries = new Array();

  /** @type {Array<StateModel>} List of states. */
  public states: Array<StateModel>;

  /** @type {Array<VillageModel>} List of villages. */
  public villages: Array<VillageModel>;

  /** @type {any} Generic item. */
  public item: any;

  /** @type {any} Selected state item. */
  public stateitem: any;

  /** @type {any} Selected village item. */
  public villageItem: any;

  /** @type {string} Search keyword. */
  public keyword: string;

  /** @type {string} State search keyword. */
  public statekeyword: string;

  /** @type {string} Village search keyword. */
  public villagekeyword: string;

  /** @type {string} Selected country name. */
  public country: string;

  /** @type {boolean} Flag indicating if village changed. */
  public villageChanged: boolean = false;

  /** @type {boolean} Flag to disable state/village fields. */
  public stateVillageDisabled: boolean = false;

  /** @type {boolean} Flag to prevent double clicks. */
  public locationCreationDoubleClick: boolean;

  /** @type {Array<BusinessEntity>} List of provider businesses. */
  public providerBusinessEntities: Array<BusinessEntity>;

  /** @type {Array<PersonsModel>} List of persons by provider business. */
  public personsByProviderBusinessId: Array<PersonsModel>;

  /** @type {boolean} Flag for Provider vs SME selection. */
  public providerOrSme: boolean;

  /** @type {Array<any>} Provider chat user config. */
  public providerChatUser = new Array();

  /** @type {string | ArrayBuffer} Login user business icon. */
  public loginUserBusinessIcon: string | ArrayBuffer;

  /** @type {boolean} Flag for country dropdown. */
  public isCountryDropdown = false;

  /** @type {boolean} Flag for state dropdown. */
  public isStateDropdown = false;

  /** @type {boolean} Flag for village dropdown. */
  public isVillageDropdown = false;

  /** @type {any[]} Filtered countries list. */
  public filteredCountriesList: any[] = [];

  /** @type {any[]} Filtered states list. */
  public filteredStatesList: any[] = [];

  /** @type {any[]} Filtered villages list. */
  public filteredVillagesList: any[] = [];

  /** @type {any[]} Limited villages list. */
  public limitedVillagesList: any[] = [];

  /** @type {string} Country search text. */
  public countrySearch: string = '';

  /** @type {string} State search text. */
  public stateSearch: string = '';

  /** @type {string} Village search text. */
  public villageSearch: string = '';

  /** @type {string} Action type (add/edit). */
  public actionType: string;

  /** @type {Array<PersonsModel>} List of business admin persons. */
  public businessAdminPersons: Array<PersonsModel>;

  /** @type {any} Feed provider person ID. */
  public feedProviderPersonId: any;

  /** @type {boolean} List loader flag. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag for filtering state. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag for empty list. */
  public noList: boolean = false;

  /** @type {any} Scroll timeout. */
  private scrollTimeout: any;

  /** @type {any} Search timeout. */
  searchTimeout: any;

  /** @type {number} Form field count. */
  public formFieldCount: number = 0;

  /** @type {boolean} Flag to show form. */
  public showForm: boolean = false;

  /** @type {boolean} Flag for country change. */
  public isCountryChanged: boolean = false;

  /** @type {boolean} Flag for provider selection. */
  public isProviderSelected: boolean = false;

  /** @type {boolean} Flag for SME selection. */
  public isSmeSElected: boolean = false;

  /** @type {boolean} Flag for Buyer selection. */
  public isBuyerSelected: boolean = false;

  /** @type {Date} Max date for SME contract. */
  SMEMaxDate = new Date();

  /** @type {Date} Max date for Buyer contract. */
  BuyerMaxDate = new Date();

  /** @type {{startDate: moment.Moment, endDate: moment.Moment}} Selected SME date range. */
  SMESelected: { startDate: moment.Moment, endDate: moment.Moment };

  /** @type {{startDate: moment.Moment, endDate: moment.Moment}} Selected Buyer date range. */
  BuyerSelected: { startDate: moment.Moment, endDate: moment.Moment };


  /**
   * Constructor for BusinessEntityFarmsComponent.
   * Initializes services and models.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {PersonsService} personService - Service for person operations.
   * @param {BusinessTypeService} businessTypeService - Service for business types.
   * @param {BusinessEntityService} businessEntityService - Service for business entities.
   * @param {FarmSiteService} farmSiteService - Service for farm sites.
   * @param {CustomerPondService} customerPondService - Service for customer ponds.
   * @param {WaterTypeService} waterTypeService - Service for water types.
   * @param {SoilTypeService} soilTypeService - Service for soil types.
   * @param {GeoLocationService} geoLocationService - Service for geo-location.
   * @param {PersonsService} personsService - Service for persons (duplicate injection).
   * @param {ImageDetailsService} imageDetailService - Service for image details.
   * @param {Router} router - Angular Router.
   * @param {ActivatedRoute} route - Active route service.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   * @param {CountriesAPIService} countriesAPIService - Service for fetching countries.
   * @param {StatesAPIService} statesAPIService - Service for fetching states and villages.
   * @param {NgZone} ngZone - Angular Zone service.
   * @param {ChangeDetectorRef} cdr - Change detector reference.
   */
  constructor(private toasterService: ToastrService, public personService: PersonsService, public businessTypeService: BusinessTypeService, public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public customerPondService: CustomerPondService, public waterTypeService: WaterTypeService, public soilTypeService: SoilTypeService, public personsService: PersonsService, public imageDetailService: ImageDetailsService,
    public router: Router, public route: ActivatedRoute, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService, public countriesAPIService: CountriesAPIService, public statesAPIService: StatesAPIService, private ngZone: NgZone, private cdr: ChangeDetectorRef) {
    this.farmSite = new FarmSiteModel();
    this.bioSecurity = new BioSecurity();
    this.smeContract = new SMEContract();
    this.buyerContract = new BuyerContract();
    this.contract = new Contract();
    this.address = new IAddress();
    this.waterType = new IWaterType();
    this.soilType = new ISoilType();
    this.villagesList = [];
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Initializes form groups and validators.
   * - Fetches initial data (Countries, Business Types, etc.).
   * - Handles route parameters for edit/add modes.
   */
  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.farmSites = [];
    this.farmSitesList = [];
    this.filteredFarmSites = [];
    this.isFiltering = false;

    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.appPermission = this.route.snapshot.data['rolepermission'];
    // // validation for farm site
    this.addFarmsiteForm = this.formBuilder.group({
      farmNameAtLocation: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(30), Validators.pattern('^[a-zA-Z\\s\\W]*$')]],
      yearStarted: ['', Validators.required],
      waterTypeId: ['', Validators.required],
      soilTypeId: ['', Validators.required],
      ownership: ['', Validators.required],
      countryName: ['', [Validators.required]],
      stateName: ['', [Validators.required]],
      villageName: ['', [Validators.required]],
      totalFarmArea: ['', Validators.pattern('^[0-9]*.[0-9]*$')],
      providertech: [null],
      enablechartuser: [null],
      persontype: [null],
      numberOfGrowoutPonds: [null],
      waterspreadArea: [null],
      numberOfNurseries: [null],
      bioFencing: [null],
      bioHWandFA: [null],
      bioCrabFencing: [null],
      bioReservoirs: [null],
      bioBirdFencing: [null],
      bioFilters: [null],
      ETSOnPremises: [null],
      labOnPremises: [null]
    });

    // Calculate field count after form is built
    this.formFieldCount = Object.keys(this.addFarmsiteForm.controls).length;
    // // validation for farm site
    try {
      this.keyword = 'name';
      this.statekeyword = 'name';
      this.villagekeyword = 'name';
      this.locationCreationDoubleClick = false;
      this.stateVillageDisabled = false;
      this.submitButton = false;
      this.updateButton = false;
      /*login User data*/
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.loginBusinessID = localStorage.businessID;
      /*login User data*/
      this.pondArr = [];
      this.countries = await this.countriesAPIService.getAllCountriesList();
      if (this.countries) {
        this.countrySearch = 'India';
        this.country = 'India';
        const indianCountryIndexPosition = this.countries.findIndex((country) => country.name == 'India');
        if (indianCountryIndexPosition != -1) {
          const objectToMove = this.countries.splice(indianCountryIndexPosition, 1);
          this.countries.unshift(objectToMove[0]);
          this.filteredCountriesList = this.countries;
          this.getStates();
        }
      }
      if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        let persons = await this.personService.getPersonBusinessPersonsByBusinessID(this.loginBusinessID);
        this.businessAdminPersons = persons.persons.filter(tech => tech && tech.personType && tech.personType[0] && tech.personType[0].name == 'Provider Technician')
      }
      this.route.params.subscribe(params => {
        this.paramsbusinessId = params.businessID;
      });
      this.address = new IAddress();
      this.waterareavalidation = false;
      this.waterType.waterTypeId = undefined;
      this.soilType.soilTypeId = undefined;
      this.contract.feedProviderID = '';
      this.contract.feedProviderPersonID = '';
      this.bioFencing = false;
      this.bioHWandFA = false;
      this.bioCrabFencing = false;
      this.bioReservoirs = false;
      this.bioBirdFencing = false;
      this.bioFilters = false;
      this.villagesList = [];
      this.yearStartedArr = [];
      const curryear = new Date().getFullYear();
      for (let i = 1947; i < curryear; i++) {
        this.yearStartedArr.push(i + 1);
      }
      this.inIt();
      this.strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      /********** Update farmsite **********/
      this.getStates();
      this.selectedYear = '';
      // Business Details registraion validation
      this.sidenavaction.x_navigation();
      this.address.village = '';
      this.businessTypes = await this.businessTypeService.getAllBusinessTypes();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests for all injected services.
   */
  public ngOnDestroy(): void {
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.waterTypeService.cancelOnGoingHttpRequests();
    this.soilTypeService.cancelOnGoingHttpRequests();
    this.personsService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
    this.imageDetailService.cancelOnGoingHttpRequests();
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the template.
   * @returns {AbstractControl} Form controls.
   */
  public get f() {
    return this.addFarmsiteForm.controls;
  }

  private batchLoader: BatchLoader<any>;

  /**
   * @method inIt
   * @async
   * @description Initializes data loading for the component, including business entity details and farm sites.
   */
  public async inIt() {
    try {
      this.locationCreationDoubleClick = false;
      this.businessEntity = await this.businessEntityService.getOneByID(this.paramsbusinessId);
      if (this.businessEntity) {
        if (this.businessEntity && this.businessEntity.branding_attachments && this.businessEntity.branding_attachments.length > 0 && this.businessEntity.branding_attachments[0] && this.businessEntity.branding_attachments[0].file_path) {
          let filePath = this.businessEntity.branding_attachments[0].file_path;
          const file = await this.businessEntityService.downloadFileFromS3Bucket(filePath);
          file.subscribe(fileBlob => {
            const readFile = new FileReader();
            readFile.readAsDataURL(fileBlob);
            readFile.onload = () => {
              this.loginUserBusinessIcon = readFile.result;
              if (this.loginUserBusinessIcon) {
                localStorage.setItem('loginBusinessIcon', filePath);
              }
            };
          });
        }
      }
      this.loginUserBusinessIcon = localStorage.getItem('loginBusinessIcon'); // login person Business name
      this.farmSites = await this.farmSiteService.getFarmeSitesByFarmId(this.paramsbusinessId);
      this.pgLoad = true;
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.stateVillageDisabled = false;
      this.countryCode = this.businessEntity.address[0].countryCode;
      this.waterTypes = await this.waterTypeService.getAllWaterTypes();
      this.soilTypes = await this.soilTypeService.getAllSoilTypes();
      this.personsByBusinessId = await this.personsService.getPersonsByBusinessID(this.paramsbusinessId);
      this.personsByProviderBusinessId = await this.personsService.getPersonsbyBusinessID(this.paramsbusinessId);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
    * @method goToCustomerPonds
    * @description Navigates to the details page of a specific farm site.
    * @param {FarmSiteModel} farmSite - The selected farm site.
    */
  public async goToCustomerPonds(farmSite: FarmSiteModel) {
    const business = await this.businessEntityService.getOneByID(farmSite.businessID);
    if (business?.businessType?.[0]?.businessTypeID == '618916f2b4391c6d48137e3d') {
      this.router.navigate(['/home/customer-ponds', farmSite.id]);
      return;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers batch loading.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Checks if scroll position is near bottom and loads next batch.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredFarmSites : this.farmSites,
      (updatedList) => {
        this.farmSitesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method checkPincode
   * @async
   * @description Fetches latitude and longitude for the given location details.
   * @param {string} village - Village name.
   * @param {string} state - State name.
   * @param {string} mandal - Mandal name.
   * @param {string} district - District name.
   */
  public async checkPincode(village: string, state: string, mandal: string, district: string) {
    try {
      this.latLongs = await this.farmSiteService.latLongsData(village, state, mandal, district);
      if (this.latLongs.results.length > 0) {
        this.address.latitude = this.latLongs.results[0].geometry.location.lat;
        this.address.longitude = this.latLongs.results[0].geometry.location.lng;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method addFarmSite
   * @async
   * @description Submits the form to create a new farm site.
   * Validates input, constructs payload, and calls service.
   */
  public async addFarmSite() {

    this.submitted = true;
    this.addFarmsiteForm.markAllAsTouched();

    if (this.addFarmsiteForm.invalid) return false;

    if (this.waterType.waterTypeId && _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)])) {
      const watertypedata = _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)]);
      this.waterType.name = watertypedata[0].name;
    }
    // changed same for address and waterTypes
    if (this.soilType.soilTypeId && _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)])) {
      const soiltypedata = _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)]);
      this.soilType.name = soiltypedata[0].name;
    }
    this.farmSite.waterType = this.waterType;
    this.farmSite.soilType = this.soilType;
    this.farmSite.bioSecurity = [];
    if (this.bioFencing) {
      this.farmSite.bioSecurity.push({ name: 'Fencing' });
    }
    if (this.bioHWandFA) {
      this.farmSite.bioSecurity.push({ name: 'Hand Wash and Footbath Area' });
    }
    if (this.bioCrabFencing) {
      this.farmSite.bioSecurity.push({ name: 'Crab Fencing' });
    }
    if (this.bioReservoirs) {
      this.farmSite.bioSecurity.push({ name: 'Reservoirs' });
    }
    if (this.bioBirdFencing) {
      this.farmSite.bioSecurity.push({ name: 'Bird Fencing' });
    }
    if (this.bioFilters) {
      this.farmSite.bioSecurity.push({ name: 'Filters' });
    }
    // Checck with this
    this.farmSite.businessID = this.farmbusinessid;
    if (this.feedProviderPersonId) {
      this.farmSite.contract = [
        {
          feedProviderID: "true",
          feedProviderPersonID: this.feedProviderPersonId,
          startDate: null,
          endDate: null
        }
      ];
    } else {
      this.farmSite.contract = [
        {
          feedProviderID: "true",
          feedProviderPersonID: "true",
          startDate: null,
          endDate: null
        }
      ];
    }
    // Need to be Removed Post API side changes
    this.farmSite.SMEContract = [
      {
        startDate: "",
        endDate: "",
        isCompleted: true
      }
    ]
    this.farmSite.buyerContract = [
      {
        startDate: "",
        endDate: "",
        isCompleted: true
      }
    ]
    /*address*/
    this.farmSite.address = {
      village: this.address.village.toLowerCase().includes(',') ? this.address.village.split(',')[0] : this.address.village,
      taluk: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[1] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[0] : this.address.state),
      state: this.address.state,
      pincode: this.address.pincode,
      mandal: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[1] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[0] : this.address.state),
      district: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[2] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[1] : this.address.state),
      latitude: this.address.latitude,
      longitude: this.address.longitude,
      country: this.countrySearch
    };
    this.farmSite.personType = this.farmSite.chatUserConfig;
    this.farmSite.businessID = this.paramsbusinessId
    try {
      this.locationCreationDoubleClick = true;
      this.farmSite = await this.farmSiteService.create(this.farmSite);
      this.toasterService.success('Farm site created successfully', '', {
        timeOut: 2000
      });
      this.locationCreationDoubleClick = false;
      this.showForm = false;
      this.farmSite = new FarmSiteModel();
      this.villageChanged = false;
      this.stateVillageDisabled = false;
      this.farmSite.contract = [];
      this.contract.feedProviderID = '';
      this.contract.feedProviderPersonID = '';
      this.persons = undefined;
      this.address.village = '';
      this.address.taluk = '';
      this.address.state = '';
      this.address.pincode = undefined;
      this.address.mandal = '';
      this.address.district = '';
      this.waterType.waterTypeId = undefined;
      this.soilType.soilTypeId = undefined;
      this.bioFencing = undefined;
      this.bioHWandFA = undefined;
      this.bioCrabFencing = undefined;
      this.bioReservoirs = undefined;
      this.bioBirdFencing = undefined;
      this.bioFilters = undefined;
      this.villageItem = undefined;
      this.stateitem = undefined;
      this.item = undefined;
      this.country = undefined;
      this.farmSite.ownership = undefined;
      this.farmSite.yearStarted = undefined;
      this.filteredVillagesList = new Array();
      this.filteredStatesList = new Array();
      this.address = new IAddress();
      this.closeForm();
      await this.inIt();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.locationCreationDoubleClick = false;
      this.submitButton = true;
    }
  }

  /**
   * @method changewaterspreadare
   * @description Validates that water spread area is not greater than total farm area.
   * Sets `waterareavalidation` flag.
   */
  public changewaterspreadare() {
    if (Number(this.farmSite.waterSpreadArea) >= Number(this.farmSite.totalFarmArea)) {
      this.waterareavalidation = true;
    } else {
      this.waterareavalidation = false;
    }
  }

  /**
   * @method numberValidation
   * @description Validates input to allow only numbers and decimals.
   * @param {any} event - The keyboard event.
   */
  public numberValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Allow numbers, the dot (decimal point), and the Backspace key
    if (
      !/^\d$/.test(key) && // Check if the entered character is a number
      key !== '.' && // Check if the entered character is a dot (decimal point)
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Allow only one dot (decimal point)
    if (key === '.' && value.includes('.')) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot
    if (key === '.' && value === '') {
      event.preventDefault();
    }
  }

  /**
   * @method updateFarmSite
   * @async
   * @description Submits the form to update an existing farm site.
   * Validates input, constructs payload, and calls service.
   */
  public async updateFarmSite() {
    this.submitted = true;
    this.addFarmsiteForm.markAllAsTouched();
    // stop here if form is invalid
    if (this.addFarmsiteForm.invalid) {
      return;
    }
    // stop here if form is invalid
    try {
      // this.pinCodes = await this.businessEntityService.checkPincode(this.address.pincode, this.countryCode);
      // if (this.pinCodes && this.pinCodes.result.length > 0) {
      this.farmSite.bioSecurity = [];
      if (this.bioFencing) {
        this.farmSite.bioSecurity.push({ 'name': 'Fencing' });
      }
      if (this.bioHWandFA) {
        this.farmSite.bioSecurity.push({ 'name': 'Hand Wash and Footbath Area' });
      }
      if (this.bioCrabFencing) {
        this.farmSite.bioSecurity.push({ 'name': 'Crab Fencing' });
      }
      if (this.bioReservoirs) {
        this.farmSite.bioSecurity.push({ 'name': 'Reservoirs' });
      }
      if (this.bioBirdFencing) {
        this.farmSite.bioSecurity.push({ 'name': 'Bird Fencing' });
      }
      if (this.bioFilters) {
        this.farmSite.bioSecurity.push({ 'name': 'Filters' });
      }
      this.waterTypes = await this.waterTypeService.getAllWaterTypes();
      if (this.waterType.waterTypeId && _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)])) {
        const watertypedata = _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)]);
        this.waterType.name = watertypedata[0].name;
      }
      this.soilTypes = await this.soilTypeService.getAllSoilTypes();
      if (this.soilType.soilTypeId && _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)])) {
        const soiltypedata = _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)]);
        this.soilType.name = soiltypedata[0].name;
      }
      this.farmSite.waterType = this.waterType;
      this.farmSite.soilType = this.soilType;
      if (this.feedProviderPersonId) {
        this.farmSite.contract = [
          {
            feedProviderID: "true",
            feedProviderPersonID: this.feedProviderPersonId,
            startDate: null,
            endDate: null
          }
        ];
      } else {
        this.farmSite.contract = [
          {
            feedProviderID: "true",
            feedProviderPersonID: "true",
            startDate: null,
            endDate: null
          }
        ];
      }
      // Need to be Removed Post API side changes
      this.farmSite.SMEContract = [
        {
          startDate: "",
          endDate: "",
          isCompleted: true
        }
      ]
      this.farmSite.buyerContract = [
        {
          startDate: "",
          endDate: "",
          isCompleted: true
        }
      ]
      // address
      if (this.villageChanged == true) {
        this.farmSite.address = {
          village: this.address.village.toLowerCase().includes(',') ? this.address.village.split(',')[0] : this.address.village,
          taluk: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[1] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[0] : this.address.state),
          state: this.address.state,
          pincode: this.address.pincode,
          mandal: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[1] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[0] : this.address.state),
          district: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[2] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[1] : this.address.state),
          latitude: this.address.latitude,
          longitude: this.address.longitude,
          country: this.countrySearch
        };
      }
      this.farmSite.businessID = this.paramsbusinessId;
      this.locationCreationDoubleClick = true;
      const updateFarmSite = Object.assign({}, this.farmSite);
      delete updateFarmSite.id;
      this.farmSite = await this.farmSiteService.updateOneByID(this.farmSite.id, updateFarmSite);
      this.toasterService.success('Farmsite updated successfully', '', {
        timeOut: 2000
      });
      this.stateVillageDisabled = false;
      this.farmSite = new FarmSiteModel();
      this.address = new Address();
      this.stateSearch = '';
      this.villageSearch = '';
      this.contract.feedProviderID = '';
      this.contract.feedProviderPersonID = '';
      this.villageChanged = false;
      this.submitted = false;
      this.farmSite.contract = [];
      this.address.pincode = undefined;
      this.address.village = '';
      this.waterType.waterTypeId = undefined;
      this.soilType.soilTypeId = undefined;
      this.bioFencing = undefined;
      this.bioHWandFA = undefined;
      this.bioCrabFencing = undefined;
      this.bioReservoirs = undefined;
      this.bioBirdFencing = undefined;
      this.bioFilters = undefined;
      this.villageItem = undefined;
      this.stateitem = undefined;
      this.item = undefined;
      this.country = undefined;
      this.farmSite.ownership = undefined;
      this.farmSite.yearStarted = undefined;
      this.filteredVillagesList = new Array();
      this.filteredStatesList = new Array();
      this.address = new IAddress();
      this.closeForm();
      this.inIt();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.locationCreationDoubleClick = false;
      this.updateButton = true;
    }
  }

  /**
   * @method farmSiteDetailsByID
   * @async
   * @description Fetches farm site details by ID for editing.
   * @param {any} farmSiteID - The farm site ID.
   */
  public async farmSiteDetailsByID(farmSiteID: any) {
    this.showForm = true;
    this.updateButton = true;
    this.submitButton = false;

    this.locationCreationDoubleClick = false;

    try {
      const foundFarmsite = await this.farmSiteService.getOneByID(farmSiteID)
      this.farmSite = foundFarmsite ? { ...foundFarmsite } : new FarmSiteModel(); // clone object
      this.address = this.farmSite.address;
      this.farmSite.yearStarted = Number(this.farmSite.yearStarted)
      if (this.address) {
        this.countrySearch = this.address.country || 'India';
        this.country = this.address.country || 'India';
        this.addFarmsiteForm.get('countryName')!.setValue(this.address.country);
        this.getStates();
        this.addFarmsiteForm.get('stateName')!.setValue(this.address.state);
        const stateObj = this.states.find(obj => obj.name == this.address.state);
        this.getVillages(stateObj);
        this.villageSearch = this.address.state == this.address.district ? this.address.village : (this.address.village == this.address.taluk ? this.address.village + ' , ' + this.address.district : this.address.village + ' , ' + this.address.mandal + ' , ' + this.address.district);
        this.addFarmsiteForm.get('villageName')!.setValue(this.villageSearch);
      }
      this.villageChanged = false;
      if (this.farmSite.ownership == '') {
        this.farmSite.ownership = undefined;
      }
      if (this.farmSite.waterType) {
        this.waterType = this.farmSite.waterType;
        this.waterType.waterTypeId = this.farmSite.waterType.waterTypeId;
      } else {
        this.farmSite.waterType = undefined;
      }
      if (this.farmSite.soilType) {
        this.soilType = this.farmSite.soilType;
        this.soilType.soilTypeId = this.farmSite.soilType.soilTypeId;
      } else {
        this.farmSite.waterType = undefined;
      }
      if (this.farmSite && this.farmSite.contract.length > 0) {
        this.feedProviderPersonId = this.farmSite.contract[0].feedProviderPersonID;
      }
      if (this.farmSite.ETSOnPremises == true) {
        this.farmSite.ETSOnPremises = true;
      } else {
        this.farmSite.ETSOnPremises = false;
      }
      if (this.farmSite.labOnPremises == true) {
        this.farmSite.labOnPremises = true;
      } else {
        this.farmSite.labOnPremises = false;
      }
      if (this.farmSite.bioSecurity) {
        if (_.find(this.farmSite.bioSecurity, { 'name': 'Bird Fencing' })) {
          this.bioBirdFencing = true;
        }
        if (_.find(this.farmSite.bioSecurity, { 'name': 'Fencing' })) {
          this.bioFencing = true;
        }
        if (_.find(this.farmSite.bioSecurity, { 'name': 'Hand Wash and Footbath Area' })) {
          this.bioHWandFA = true;
        }
        if (_.find(this.farmSite.bioSecurity, { 'name': 'Crab Fencing' })) {
          this.bioCrabFencing = true;
        }
        if (_.find(this.farmSite.bioSecurity, { 'name': 'Reservoirs' })) {
          this.bioReservoirs = true;
        }
        if (_.find(this.farmSite.bioSecurity, { 'name': 'Filters' })) {
          this.bioFilters = true;
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method closeForm
   * @description Resets the form and component state to default.
   */
  public closeForm() {
    this.addFarmsiteForm.reset();
    this.showForm = false;
    this.farmSite = new FarmSiteModel();
    this.address = new Address();
    this.inIt();
    this.villageChanged = false;
    this.waterareavalidation = false;
    this.submitted = false;
    this.farmSite.contract = [];
    this.persons = [];
    this.address.pincode = undefined;
    this.address.village = '';
    this.address.taluk = '';
    this.address.mandal = '';
    this.address.district = '';
    this.address.state = '';
    this.waterType.waterTypeId = undefined;
    this.soilType.soilTypeId = undefined;
    this.bioFencing = undefined;
    this.bioHWandFA = undefined;
    this.bioCrabFencing = undefined;
    this.bioReservoirs = undefined;
    this.bioBirdFencing = undefined;
    this.bioFilters = undefined;
    this.farmSite.bioSecurity = undefined;
    this.item = undefined;
    this.countrySearch = undefined;
    this.stateSearch = undefined;
    this.villageSearch = undefined;
    this.stateitem = undefined;
    this.villageItem = undefined;
    this.countrySearch = 'India';
    this.country = 'India';
    this.getStates();
    this.filteredVillagesList = new Array();
    this.filteredStatesList = new Array();
  }

  /**
   * @method closeAllDropdowns
   * @description Closes all custom dropdowns.
   */
  public closeAllDropdowns() {
    this.isCountryDropdown = false;
    this.isStateDropdown = false;
    this.isVillageDropdown = false;
  }

  /**
   * @method toggleDropdown
   * @description Toggles the visibility of a specific dropdown.
   * @param {'country' | 'state' | 'village'} type - The dropdown type.
   */
  public toggleDropdown(type: 'country' | 'state' | 'village') {
    this.isCountryDropdown = type === 'country';
    this.isStateDropdown = type === 'state';
    this.isVillageDropdown = type === 'village';
  }

  /**
   * @method openForm
   * @description Opens the form for adding a new farm site.
   * Sets default values (e.g., Country = India).
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.country = 'India',
      this.addFarmsiteForm.get('countryName')!.setValue(this.country);
    this.countrySearch = this.country;
    this.isCountryChanged = true;
    this.getStates();
    this.isSMEChecked = false;
    this.providerOrSme = null;
  }

  /**
   * @method cancelData
   * @description Resets deletion selection.
   */
  public cancelData() {
    this.selectedDeletedataType = undefined;
    this.pondArr = [];
  }

  /**
   * @method decimalFilter
   * @description Validates input to accept only two digits after decimal point.
   * @param {any} event - The keyboard event.
   */
  public decimalFilter(event: any) {
    this.growOutPondsValidation = false;
    const reg = /^-?\d*(\.\d{0,2})?$/;
    let input = event.target.value + String.fromCharCode(event.charCode);
    if (!reg.test(input)) {
      this.growOutPondsValidation = true;
      event.preventDefault();
    }
  }

  /**
   * @method filterCountries
   * @description Filters the list of countries based on user input.
   * @side-effect Updates `filteredCountriesList`.
   */
  public filterCountries(): void {
    const value = (this.addFarmsiteForm.get('countryName')!.value || '') as CountriesModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredCountriesList = this.countries.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method coutrySelection
   * @description Handles country selection. Updates form and fetches states.
   * @param {any} country - The selected country object.
   */
  public coutrySelection(country: any): void {
    this.addFarmsiteForm.get('countryName')!.setValue(country.name);
    this.isCountryDropdown = false;
    this.countrySearch = country.name;
    this.country = country.name;
    this.isCountryChanged = true;
    this.getStates();
  }

  /**
   * @method filterStates
   * @description Filters the list of states based on user input.
   * @side-effect Updates `filteredStatesList`.
   */
  public filterStates(): void {
    const value = (this.addFarmsiteForm.get('stateName')!.value || '') as StateModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredStatesList = this.states.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method stateSelection
   * @description Handles state selection. Updates form and fetches villages.
   * @param {any} state - The selected state object.
   */
  public stateSelection(state: any): void {
    this.addFarmsiteForm.get('stateName')!.setValue(state.name);
    this.address.state = state.name;
    this.stateSearch = state.name;
    this.isStateDropdown = false;
    this.getVillages(state);
  }

  /**
   * @method getStates
   * @description Retrieves and populates the list of states based on the selected country.
   */
  public getStates() {
    this.states = [];
    this.villages = [];
    this.filteredStatesList = [];
    this.filteredVillagesList = [];
    if (this.submitButton || (this.updateButton && this.isCountryChanged)) {
      this.addFarmsiteForm.get('stateName')!.setValue('');
      this.addFarmsiteForm.get('villageName')!.setValue('');
    }
    const selectedCountryObject = this.countries.find((country: CountriesModel) => country.name == this.country);
    this.states = selectedCountryObject.states;
    this.filteredStatesList = this.states;
    this.stateVillageDisabled = false;
  }

  /**
    * @method getVillages
    * @async
    * @description Retrieves and populates the list of villages based on the selected state.
    */
  public isLoadingVillages = false;
  public isLoadingMore = false;
  public villagesLoadError = false;
  public visibleVillages: any[] = [];
  public CHUNK_SIZE = 50;
  private currentIndex = 0;
  private searchText = '';
  private isLoadTriggered = false;
  public noDataFound = false;

  public async getVillages(state: any) {
    this.resetVillages();
    this.isLoadingVillages = true;
    this.villagesLoadError = false;
    this.noDataFound = false;

    try {
      const response = await this.statesAPIService.getVillagesByStates(state.name);
      if (response?.[0]?.city_town_village?.length) {
        this.villages = response[0].city_town_village;
        this.loadNextChunk();
      } else {
        this.villagesLoadError = true;
      }
    } catch (err) {
      this.villagesLoadError = true;
    } finally {
      this.isLoadingVillages = false;
    }
  }

  /**
   * @method loadNextChunk
   * @private
   * @description Loads the next chunk of villages for infinite scroll.
   */
  private loadNextChunk() {
    const source = this.searchText
      ? this.villages.filter(v =>
        v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
      )
      : this.villages;

    if (source.length === 0) {
      this.noDataFound = true;
      return;
    }

    this.noDataFound = false; // reset when data exists
    const nextSet = source.slice(this.currentIndex, this.currentIndex + this.CHUNK_SIZE);
    this.visibleVillages = [...this.visibleVillages, ...nextSet];
    this.currentIndex += this.CHUNK_SIZE;
  }

  /**
   * @method onVillageScroll
   * @description Handles scroll event on village dropdown to load more items.
   */
  public onVillageScroll() {
    if (this.isLoadTriggered || this.isLoadingMore) return;

    const remaining = this.getFilteredList()?.length - this.visibleVillages.length;
    if (remaining > 0) {
      this.isLoadTriggered = true;
      this.isLoadingMore = true;

      setTimeout(() => {
        this.ngZone.run(() => {
          this.loadNextChunk();
          this.isLoadingMore = false;
          this.isLoadTriggered = false;
          this.cdr.detectChanges();
        });
      }, 300);
    }
  }

  /**
   * @method filterVillages
   * @description Filters the village list based on user input.
   * @param {any} event - The input event.
   */
  public filterVillages(event: any) {
    this.searchText = event.target.value;
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.loadNextChunk();
  }

  /**
   * @method getFilteredList
   * @private
   * @description Helper to get filtered list of villages.
   */
  private getFilteredList() {
    return this.searchText
      ? this.villages.filter(v =>
        v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
      )
      : this.villages;
  }

  /**
   * @method resetVillages
   * @private
   * @description Resets village data structures.
   */
  private resetVillages() {
    this.villages = [];
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
    this.isLoadTriggered = false;
    this.noDataFound = false;
    this.addFarmsiteForm.get('villageName')?.setValue('');
  }

  /**
   * @method onVillageFocus
   * @description Handles focus event on village input.
   */
  public onVillageFocus() {
    if (!this.searchText) {
      // If no search text, just reload first chunk
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    } else {
      // If search exists, reload search result
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    }
  }

  /**
   * @method onVillageBlur
   * @description Handles blur event on village input.
   */
  public onVillageBlur() {
    if (this.noDataFound || this.visibleVillages.length == 0) {
      this.clearVillageField();
    }
  }

  /**
   * @method clearVillageField
   * @private
   * @description Clears the village input field.
   */
  private clearVillageField() {
    this.addFarmsiteForm.get('villageName')?.setValue('');
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
  }

  /**
   * @method villageSelection
   * @description Handles village selection. Updates form and triggers geocoding.
   * @param {any} village - The selected village object.
   */
  public villageSelection(village: any): void {
    this.addFarmsiteForm.get('villageName')!.setValue(village.city_town_village_name);
    this.isVillageDropdown = false;
    this.villageSearch = village.city_town_village_name;
    this.address.village = this.villageSearch;
    if (this.address.village) {
      this.villageChanged = true;
      const villageParts = this.address.village.toLowerCase().split(',');
      const district = villageParts.length > 1 ? villageParts[1] : '';
      const state = villageParts.length > 2 ? villageParts[2] : this.address.state;
      this.checkPincode(this.address.village, this.address.state, district, state);
    }
  }
}